export interface NoticeCompositionSnapshot {
  sourceJson: string;
  selectedBlockId: string | null;
  structured?: boolean;
}

export interface NoticeCompositionHistoryOptions {
  maxEntries?: number;
  maxUtf8Bytes?: number;
  coalesceMs?: number;
}

interface RetainedSnapshot {
  snapshot: NoticeCompositionSnapshot;
  bytes: number;
}

/** An in-memory history of exact sources, shared by Page and Fields editing. */
export class NoticeCompositionHistory {
  private readonly maxEntries: number;
  private readonly maxUtf8Bytes: number;
  private readonly coalesceMs: number;
  private readonly encoder = new TextEncoder();
  private entries: RetainedSnapshot[] = [];
  private position = -1;
  private retainedBytes = 0;
  private groupKey: string | undefined;
  private groupTime: number | undefined;

  constructor(options: NoticeCompositionHistoryOptions = {}) {
    this.maxEntries = options.maxEntries ?? 100;
    this.maxUtf8Bytes = options.maxUtf8Bytes ?? 2 * 1024 * 1024;
    this.coalesceMs = options.coalesceMs ?? 750;
    if (
      !Number.isSafeInteger(this.maxEntries) ||
      this.maxEntries < 1 ||
      !Number.isSafeInteger(this.maxUtf8Bytes) ||
      this.maxUtf8Bytes < 1 ||
      !Number.isFinite(this.coalesceMs) ||
      this.coalesceMs < 0
    )
      throw new RangeError('Notice history limits must be finite and bounded.');
  }

  get canUndo(): boolean {
    return this.position > 0;
  }

  get canRedo(): boolean {
    return this.position >= 0 && this.position < this.entries.length - 1;
  }

  get current(): NoticeCompositionSnapshot | null {
    const retained = this.entries[this.position];
    return retained ? { ...retained.snapshot } : null;
  }

  get size(): number {
    return this.entries.length;
  }

  get utf8Bytes(): number {
    return this.retainedBytes;
  }

  /** Omit the snapshot when authorization or scope changes to release all sources. */
  reset(snapshot?: NoticeCompositionSnapshot): void {
    this.entries = [];
    this.position = -1;
    this.retainedBytes = 0;
    this.breakGroup();
    if (snapshot) this.record(snapshot);
  }

  breakGroup(): void {
    this.groupKey = undefined;
    this.groupTime = undefined;
  }

  /** Amend a server-validated representation without adding a formatting-only undo. */
  replaceCurrent(snapshot: NoticeCompositionSnapshot): boolean {
    this.breakGroup();
    const retained: RetainedSnapshot = {
      snapshot: { ...snapshot },
      bytes:
        this.encoder.encode(snapshot.sourceJson).length +
        this.encoder.encode(snapshot.selectedBlockId ?? '').length,
    };
    if (retained.bytes > this.maxUtf8Bytes) {
      this.reset();
      return false;
    }
    const current = this.entries[this.position];
    if (current) {
      this.retainedBytes -= current.bytes;
      this.entries[this.position] = retained;
    } else {
      this.entries.push(retained);
      this.position = 0;
    }
    this.retainedBytes += retained.bytes;
    this.trim();
    return true;
  }

  /**
   * Record an exact source. A shared group key coalesces consecutive typing, while
   * an omitted key creates a distinct undo boundary. The initial baseline is
   * never coalesced away. Oversized sources clear history instead of retaining
   * unbounded imported text; the caller's current source remains authoritative.
   */
  record(
    snapshot: NoticeCompositionSnapshot,
    groupKey?: string,
    now = Date.now(),
  ): boolean {
    const retained: RetainedSnapshot = {
      snapshot: { ...snapshot },
      bytes:
        this.encoder.encode(snapshot.sourceJson).length +
        this.encoder.encode(snapshot.selectedBlockId ?? '').length,
    };
    if (retained.bytes > this.maxUtf8Bytes) {
      this.reset();
      return false;
    }

    const current = this.entries[this.position];
    if (current?.snapshot.sourceJson === snapshot.sourceJson) {
      // Selecting a block is not a document edit, but the next undo/redo should
      // return the most recent selection for this exact document state.
      this.retainedBytes += retained.bytes - current.bytes;
      this.entries[this.position] = retained;
      if (groupKey === undefined) this.breakGroup();
      this.trim();
      return false;
    }

    for (const removed of this.entries.splice(this.position + 1))
      this.retainedBytes -= removed.bytes;

    const coalesce =
      groupKey !== undefined &&
      groupKey === this.groupKey &&
      this.groupTime !== undefined &&
      Number.isFinite(now) &&
      now >= this.groupTime &&
      this.coalesceMs > 0 &&
      now - this.groupTime <= this.coalesceMs &&
      this.position > 0;
    if (coalesce) {
      this.retainedBytes -= this.entries[this.position].bytes;
      this.entries[this.position] = retained;
    } else {
      this.entries.push(retained);
      this.position++;
    }
    this.retainedBytes += retained.bytes;
    this.groupKey = groupKey;
    this.groupTime = Number.isFinite(now) ? now : undefined;
    this.trim();
    return true;
  }

  undo(): NoticeCompositionSnapshot | null {
    this.breakGroup();
    if (!this.canUndo) return null;
    this.position--;
    return this.current;
  }

  redo(): NoticeCompositionSnapshot | null {
    this.breakGroup();
    if (!this.canRedo) return null;
    this.position++;
    return this.current;
  }

  private trim(): void {
    while (
      this.entries.length > this.maxEntries ||
      this.retainedBytes > this.maxUtf8Bytes
    ) {
      // After undo, a selection-only update must retain the current state even
      // when its selection metadata makes the total exceed the byte budget.
      const removed =
        this.position > 0 ? this.entries.shift() : this.entries.pop();
      if (removed) this.retainedBytes -= removed.bytes;
      if (this.position > 0) this.position--;
    }
  }
}
